import { Eye, Plus, Search, Trash2, Users } from 'lucide-react'
import { Fragment, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Layout from '../../components/Layout'
import { Badge } from '../../components/ui/badge'
import { Button } from '../../components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card'
import { Input } from '../../components/ui/input'
import { useNotification } from '../../contexts/NotificationContext'
import {
  createGroup as createGroupService,
  deleteGroup as deleteGroupService,
  fetchAllGroupsAdmin,
} from '../../services/groupService'

function formatCreatedAt(iso) {
  if (!iso) return ''
  return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}

export default function GroupManagement({ embedded = false }) {
  const Wrapper = embedded ? Fragment : Layout
  const navigate = useNavigate()
  const notify = useNotification()
  const [groups, setGroups] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [createName, setCreateName] = useState('')
  const [createDescription, setCreateDescription] = useState('')
  const [createIsPublic, setCreateIsPublic] = useState(true)
  const [createIsPrivate, setCreateIsPrivate] = useState(false)
  const [creating, setCreating] = useState(false)

  const loadGroups = async () => {
    try {
      setLoading(true)
      const data = await fetchAllGroupsAdmin()
      setGroups(data || [])
    } catch (err) {
      console.error('Error loading groups:', err)
      notify.error('Failed to load groups')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadGroups()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const filteredGroups = useMemo(() => {
    const q = searchQuery.trim().toLowerCase()
    if (!q) return groups
    return groups.filter(g =>
      g.name?.toLowerCase().includes(q) ||
      g.creator_email?.toLowerCase().includes(q)
    )
  }, [groups, searchQuery])

  const totalMembers = useMemo(
    () => groups.reduce((sum, g) => sum + (g.member_count || 0), 0),
    [groups]
  )

  const resetCreateForm = () => {
    setCreateName('')
    setCreateDescription('')
    setCreateIsPublic(true)
    setCreateIsPrivate(false)
  }

  const handleCreate = async () => {
    const name = createName.trim()
    if (!name) {
      notify.error('Group name is required')
      return
    }
    try {
      setCreating(true)
      await createGroupService({
        name,
        description: createDescription.trim() || null,
        isPublic: createIsPublic,
        isPrivate: createIsPrivate,
      })
      notify.success(`Created "${name}"`)
      setShowCreateModal(false)
      resetCreateForm()
      await loadGroups()
    } catch (err) {
      console.error('Error creating group:', err)
      notify.error(err.message || 'Failed to create group')
    } finally {
      setCreating(false)
    }
  }

  const handleDelete = async (group) => {
    const confirmed = await notify.confirm({
      title: 'Delete Group',
      description: `Delete "${group.name}"? This will remove the group, all memberships, and any prize rows tied to it. This cannot be undone.`,
      variant: 'destructive',
      confirmText: 'Delete Group',
      cancelText: 'Cancel',
    })
    if (!confirmed) return

    try {
      await deleteGroupService({ groupId: group.id })
      setGroups(prev => prev.filter(g => g.id !== group.id))
      notify.success(`Deleted "${group.name}"`)
    } catch (err) {
      console.error('Error deleting group:', err)
      notify.error(err.message || 'Failed to delete group')
    }
  }

  return (
    <Wrapper>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-2">
            <h1 className="text-3xl font-bold">Group Management</h1>
            <Button onClick={() => setShowCreateModal(true)} className="w-full sm:w-auto">
              <Plus className="h-4 w-4 mr-2" />
              Create Group
            </Button>
          </div>
          <p className="text-muted-foreground">All groups across the platform, including private ones.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">Total Groups</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{groups.length}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">Public</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{groups.filter(g => g.is_public).length}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">Total Memberships</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{totalMembers}</div>
            </CardContent>
          </Card>
        </div>

        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by group name or creator email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>All Groups</CardTitle>
            <CardDescription>View or delete any group. Member and manager management lives on the group detail page.</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-12 text-muted-foreground">Loading groups...</div>
            ) : filteredGroups.length === 0 ? (
              <div className="text-center py-12">
                <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4 opacity-50" />
                <p className="text-muted-foreground">
                  {groups.length === 0 ? 'No groups yet' : 'No groups match your search'}
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {filteredGroups.map((group) => (
                  <div
                    key={group.id}
                    className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between p-4 border rounded-lg hover:bg-muted/60 transition-colors"
                    data-testid="group-row"
                    data-group-id={group.id}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <h3 className="font-semibold truncate">{group.name}</h3>
                        <Badge variant={group.is_public ? 'purple' : 'muted'}>
                          {group.is_public ? 'Public' : 'Private'}
                        </Badge>
                        {group.private && <Badge variant="muted">Hidden</Badge>}
                      </div>
                      {group.description && (
                        <p className="text-sm text-muted-foreground mb-2 line-clamp-1">{group.description}</p>
                      )}
                      <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                        <span>{group.member_count ?? 0} {group.member_count === 1 ? 'member' : 'members'}</span>
                        <span>Created {formatCreatedAt(group.created_at)}</span>
                        {group.creator_email && <span>by {group.creator_full_name || group.creator_email}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0 w-full sm:w-auto sm:justify-end">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate(`/groups/${group.id}`)}
                      >
                        <Eye className="h-4 w-4 mr-2" />
                        View
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(group)}
                        className="text-destructive hover:text-destructive"
                        aria-label={`Delete ${group.name}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {showCreateModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <Card className="w-full max-w-md">
              <CardHeader>
                <CardTitle>Create Group</CardTitle>
                <CardDescription>Add a new group. You'll be the owner by default.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">Group Name</label>
                  <Input
                    placeholder="e.g., Austin Pickleball Club"
                    value={createName}
                    onChange={(e) => setCreateName(e.target.value)}
                    autoFocus
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">Description</label>
                  <Input
                    placeholder="Short description (optional)"
                    value={createDescription}
                    onChange={(e) => setCreateDescription(e.target.value)}
                  />
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="createIsPublic"
                    checked={createIsPublic}
                    onChange={(e) => setCreateIsPublic(e.target.checked)}
                    className="rounded"
                  />
                  <label htmlFor="createIsPublic" className="text-sm font-medium">
                    Public (anyone can join)
                  </label>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="createIsPrivate"
                    checked={createIsPrivate}
                    onChange={(e) => setCreateIsPrivate(e.target.checked)}
                    className="rounded"
                  />
                  <label htmlFor="createIsPrivate" className="text-sm font-medium">
                    Hidden from discovery (invite-only)
                  </label>
                </div>
                <div className="flex gap-2 pt-2">
                  <Button
                    variant="outline"
                    onClick={() => { setShowCreateModal(false); resetCreateForm() }}
                    className="flex-1"
                    disabled={creating}
                  >
                    Cancel
                  </Button>
                  <Button onClick={handleCreate} className="flex-1" disabled={creating}>
                    {creating ? 'Creating...' : 'Create Group'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </Wrapper>
  )
}
